import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button, Paragraph1, SmallText } from 'inpock-ui-react';
import Flicking, { ChangedEvent } from '@egjs/react-flicking';
import Agent from '@egjs/agent';
import queryString from 'query-string';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);
dayjs.extend(isBetween);

import { useCampaignV2Query, useToast, useDialog } from '@/hooks';
import {
  Navigation,
  CampaignContent,
  OptionSelectSheet,
  ToTopButton,
  Image,
  Spinner,
  Accordion,
} from '@/components';
import { useOptionSelectSheet } from '@/hooks';
import { ProductDescription } from '@/fragments';
import { Footer } from '@/components';
import { ORDER_TEMPLATE, copy, thumbnail } from '@/utils';
import { Campaign, MARKET_STATUS } from '@/apis';
import { urlFormatter } from '@/utils/urlFormatter';

import * as S from './styled';

const FLICKING_OPTIONS = {
  defaultIndex: 0,
  panelsPerView: 1,
  threshhold: 15,
  align: 'center',
};

const MAX_REVIEW_COUNT = 9999;

export function ProductV2Page() {
  const [dialogOn] = useDialog();
  const { open } = useOptionSelectSheet();
  const [fold, setFold] = useState<boolean>(false);
  const [isFold, setIsFold] = useState<boolean>(true);
  const [remainingTime, setRemainingTime] = useState<string>('');
  const toastOn = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;

  const query = queryString.parse(location.search);

  const { data: block, isFetching, isLoading, isError } = useCampaignV2Query(id as string);

  const reviewCount = block?.campaign?.review_data.counts as number;
  const reviewTitle = `리뷰 ${
    (reviewCount > MAX_REVIEW_COUNT ? `${MAX_REVIEW_COUNT}+` : reviewCount) ?? 0
  }`;

  const menu = ['상품 정보', reviewTitle];

  const [isTab, setTab] = useState<string>(menu[0]);
  const [currentIndex, setCurrentIndex] = useState(1);

  const calculateStockQuantity = (campaign: Campaign): number | null => {
    const stockQuantities: (number | null)[] = [];

    if (!campaign.options.length) stockQuantities.push(campaign.stock_quantity);

    for (const option of campaign.options) {
      if (!option.sub_options.length) stockQuantities.push(option.stock_quantity);

      for (const subOption of option.sub_options) {
        stockQuantities.push(subOption.stock_quantity);
      }
    }

    if (stockQuantities.every((quantity) => quantity === 0)) return 0;
    else return 1;
  };

  const checkSaleStatus: () => {
    disabled: boolean;
    text: string;
    purchaseBtnText?: string;
  } = () => {
    const campaign = block?.campaign;
    const marketInfo = block?.market_info;
    if (!campaign?.open_at || (!campaign?.open_until && !campaign.is_permanent))
      return { disabled: true, text: '판매 정보가 없습니다' };
    if (
      marketInfo &&
      ![
        MARKET_STATUS.ACTIVE,
        MARKET_STATUS.UNDER_REVIEW,
        MARKET_STATUS.UNDER_CARD_REVIEW,
        MARKET_STATUS.REJECTED,
      ].includes(marketInfo.market_status)
    ) {
      return { disabled: true, text: '심사중인 마켓이라 아직 구매할 수 없어요' };
    }

    const start = dayjs(campaign.open_at);
    const end = dayjs(campaign.open_until);
    const now = dayjs();
    const stockQuantity = calculateStockQuantity(campaign);

    if (campaign?.is_permanent) {
      if (!campaign.open_at) return { disabled: true, text: '판매 정보가 없습니다' };

      if (start.isAfter(now)) {
        const timeRemaining = calculateTimeRemaining(start.toDate().getTime(), 'purchase');
        return { disabled: false, text: '구매하기', purchaseBtnText: timeRemaining };
      }

      if (stockQuantity === 0) return { disabled: true, text: '상품 재고가 없어요 😢' };
      if (campaign.is_sold_out) return { disabled: true, text: '판매가 잠시 중지되었어요 😢' };

      return { disabled: false, text: '구매하기' };
    }

    if (!campaign?.open_at || !campaign?.open_until)
      return { disabled: true, text: '판매 정보가 없습니다' };

    if (start.isAfter(end)) return { disabled: true, text: '판매가 종료되었어요 😢' };

    if (start.isAfter(now)) {
      const timeRemaining = calculateTimeRemaining(start.toDate().getTime(), 'purchase');
      return { disabled: false, text: '구매하기', purchaseBtnText: timeRemaining };
    }

    if (now.isBetween(start, end)) {
      if (stockQuantity === 0) return { disabled: true, text: '상품 재고가 없어요 😢' };
      if (campaign.is_sold_out) return { disabled: true, text: '판매가 잠시 중지되었어요 😢' };
      return { disabled: false, text: '구매하기' };
    }

    return { disabled: true, text: '판매가 종료되었어요 😢' };
  };

  const handleShare = () => {
    const agent = Agent();
    const isMacIpadSafari =
      (agent.os.name === 'mac' || navigator.userAgent.toLowerCase().includes('ipad')) &&
      agent.browser.name === 'safari';
    if (isMacIpadSafari || !navigator.canShare) {
      copy(window.location.href);
      return dialogOn({ template: ORDER_TEMPLATE.share() });
    }

    navigator
      .share({
        title: `${block?.campaign?.seller} &  ${block?.campaign?.name}`,
        text: '공동구매 상품, 인포크링크에서 간편하게 구매해보세요!',
        url: `${window.location.href}?utm_medium=share_seller`,
      })
      .catch(console.error);
  };

  // v2 상품 문의 버튼 클릭
  const onClickInquiry = () => {
    if (block?.campaign.cs_channel) {
      if (block?.campaign.cs_channel.match(/^[0-9-+]+$/)) {
        return window.open(`tel:${block?.campaign.cs_channel}`, '_blank');
      }
      return window.open(urlFormatter(block?.campaign.cs_channel), '_blank');
    }
    if (block?.market_info.cs_channel) {
      if (block?.market_info.cs_channel.match(/^[0-9-+]+$/)) {
        return window.open(`tel:${block?.market_info.cs_channel}`, '_blank');
      }
      return window.open(urlFormatter(block?.market_info.cs_channel), '_blank');
    }
    return window.open(`tel:${block?.market_info.cs_phone}`, '_blank');
  };

  const calculateTimeRemaining = (time: number | string, type?: string): string => {
    const now = dayjs();
    const endTime = dayjs(time);
    const diff = endTime.diff(now);

    if (diff <= 0) {
      return '종료됨';
    }

    const duration = dayjs.duration(diff);
    const totalDays = Math.floor(duration.asDays());
    const days = totalDays;
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    if (type === 'purchase') return `${days}일 ${hours}시간 ${minutes}분 후 구매가능`;

    return `${days}일 ${hours}시간 ${minutes}분 ${seconds}초 남음`;
  };

  const updateRemainingTime = useCallback(() => {
    const campaign = block?.campaign;
    if (!campaign?.open_until) return;

    setRemainingTime(calculateTimeRemaining(campaign.open_until));
  }, [block?.campaign]);

  useEffect(() => {
    updateRemainingTime();

    const interval = setInterval(updateRemainingTime, 1000);
    return () => clearInterval(interval);
  }, [updateRemainingTime]);

  useEffect(() => {
    if (query.tab === 'review') setTab(menu[1]);
  }, [reviewCount, query.tab]);

  if (isError) navigate('/products/not-found', { replace: true });

  if (isLoading) {
    return (
      <S.SpinnerContainer>
        <Spinner color="var(--color-ui-grayscale-gray-600)" />
      </S.SpinnerContainer>
    );
  }

  return (
    <S.ContainerContent>
      <HelmetProvider>
        <Helmet>
          <title>상품 | 인포크링크</title>
          <meta property="og:title" content="상품 | 인포크링크" />
          <meta property="og:description" content="판매 중인 상품을 구경해보세요 🛍" />
        </Helmet>
      </HelmetProvider>

      <OptionSelectSheet
        seller={block?.market_info.owner_username}
        marketId={block?.market_info.market_id}
        disabled={{
          value: dayjs(block?.campaign?.open_at).isAfter(dayjs()),
          message: checkSaleStatus().purchaseBtnText ?? '',
        }}
        version="v2"
      />
      <Navigation
        brandName={`${block?.campaign?.seller} & ${block?.campaign?.name}`}
        seller={block?.market_info.owner_username}
        onClickShare={handleShare}
        marketInfo={{
          logo_image: block?.market_info.logo_image ?? '',
          logo_text: block?.market_info.logo_text ?? '',
          logo_type: block?.market_info.logo_type ?? 'text',
        }}
        version="v2"
      />

      <S.FlickingWrapper>
        <div
          className="d-flex flex-column"
          style={{ width: '100%', position: 'absolute', bottom: '0' }}
        >
          <S.IndexPill>
            <SmallText fontWeight="bold" color="white" wordBreak="keep-all" whiteSpace="nowrap">
              {currentIndex} / {block?.campaign && block?.campaign.images.length}
            </SmallText>
          </S.IndexPill>

          {!checkSaleStatus().disabled && !block?.campaign?.is_permanent && (
            <S.OrderViewConatiner>
              {dayjs(block?.campaign?.open_at).isAfter(dayjs()) ? (
                <>
                  <Paragraph1 fontWeight="regular" color="gray300">
                    공동구매 시작까지
                  </Paragraph1>
                  <Paragraph1 className="text-bold text-white">
                    {calculateTimeRemaining(
                      dayjs(block?.campaign?.open_at)
                        .toDate()
                        .getTime(),
                    )}
                  </Paragraph1>
                </>
              ) : (
                <>
                  <Paragraph1 fontWeight="regular" color="gray300">
                    남은 시간
                  </Paragraph1>
                  <Paragraph1 className="text-bold text-white">{remainingTime}</Paragraph1>
                </>
              )}
            </S.OrderViewConatiner>
          )}
        </div>

        <Flicking
          {...FLICKING_OPTIONS}
          onChanged={(e: ChangedEvent<Flicking>) => setCurrentIndex(e.index + 1)}
        >
          {block?.campaign?.images.map((item, index) => (
            <S.ImageWrapper key={index}>
              <source srcSet={thumbnail.origin(item.image)} type="image/webp" />
              <source srcSet={thumbnail.origin(item.image)} type="image/jpeg" />
              <Image src={thumbnail.origin(item.image)} height={400} alt="banner-image" />
            </S.ImageWrapper>
          ))}
        </Flicking>
      </S.FlickingWrapper>

      {block?.campaign && <CampaignContent block={block} onClick={() => setTab(menu[1])} />}
      <ProductDescription
        campaign={block?.campaign}
        fold={fold}
        setFold={setFold}
        isFold={isFold}
        setIsFold={setIsFold}
        menu={menu}
        isTab={isTab}
        setTab={setTab}
        csChannel={block?.market_info.cs_channel ?? ''}
      />
      <S.ProductDivider />
      <Accordion
        sx={{ marginBottom: '20px' }}
        removeBorder
        firstRow={<Paragraph1 fontWeight="bold">교환/환불 정책</Paragraph1>}
        headerType="custom"
      >
        <S.RefundPolicyContainer>
          <SmallText style={{ whiteSpace: 'pre-wrap' }}>
            {block?.campaign?.exchange_refund_policy ?? '판매자 별도 문의'}
          </SmallText>
        </S.RefundPolicyContainer>
      </Accordion>
      <ToTopButton show />
      <Footer seller_company_name={block?.campaign.seller_company_name ?? '에이비제트(주)'} />
      <S.FixedBottom>
        <S.GradientBox />
        <S.ButtonGroup>
          <Button
            type="default"
            size="large"
            fullWidth
            style={{ flex: 3 }}
            onClick={onClickInquiry}
          >
            상품 문의
          </Button>
          <Button
            type="primary"
            size="large"
            fullWidth
            disabled={checkSaleStatus().disabled}
            style={{ flex: 7 }}
            onClick={() => block?.campaign && open(block?.campaign)}
          >
            {checkSaleStatus()?.text}
          </Button>
        </S.ButtonGroup>
      </S.FixedBottom>
    </S.ContainerContent>
  );
}
